import * as React from "react"
import { useLocation } from "react-router-dom"
import { ViewType } from "@digits-graphql/frontend/graphql-bearer"
import { keysOf } from "@digits-shared/helpers/objectHelper"
import useSession from "@digits-shared/hooks/useSession"
import { AspectCode } from "@digits-shared/session/AspectCode"
import { createScopedStore } from "@digits-shared/state/scopedStore"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import type FrontendSession from "src/frontend/session"
import {
  legalEntityPreferenceDefinitions,
  type LegalEntityPreferenceValueMap,
} from "src/shared/config/legalEntityPreferenceKeys"
import type { PreferenceNamesByKind } from "src/shared/config/preferenceDefinitions"
import {
  userPreferenceDefinitions,
  type UserPreferenceValueMap,
} from "src/shared/config/userPreferenceKeys"

// Name-based types (object keys, not localStorage keys)
export type UserPreferenceName = keyof typeof userPreferenceDefinitions
export type UserPreferenceBooleanName = PreferenceNamesByKind<
  typeof userPreferenceDefinitions,
  "boolean"
>
export type LegalEntityPreferenceName = keyof typeof legalEntityPreferenceDefinitions

// Value type lookup by name (name → definition → key → value type)
type UserPreferenceValueByName<TName extends UserPreferenceName> =
  UserPreferenceValueMap[(typeof userPreferenceDefinitions)[TName]["key"]]

type LegalEntityPreferenceValueByName<TName extends LegalEntityPreferenceName> =
  LegalEntityPreferenceValueMap[(typeof legalEntityPreferenceDefinitions)[TName]["key"]]

// ─────────────────────────────────────────────────────────────────────────────
// Shared return types for preference hooks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Setter type for preference hooks. Pass undefined to delete the preference.
 * Supports functional updater pattern to avoid stale closure issues.
 */
type PreferenceSetter<TValue> = (
  nextValue: TValue | undefined | ((prev: TValue | undefined) => TValue | undefined)
) => void

/** Return type: [value, setter] - pass undefined to setter to delete */
type PreferenceHookReturn<TValue> = [TValue, PreferenceSetter<TValue>]

/** Return type for boolean preferences: [value, setter] */
type BooleanPreferenceHookReturn = [boolean, PreferenceSetter<boolean>]

// ─────────────────────────────────────────────────────────────────────────────
// Definition → Name reverse lookup (for passing definition objects)
// ─────────────────────────────────────────────────────────────────────────────

type UserPreferenceDefinition<TName extends UserPreferenceName> =
  (typeof userPreferenceDefinitions)[TName]
type AnyUserPreferenceDefinition = UserPreferenceDefinition<UserPreferenceName>

type UserPreferenceNameFromDefinition<TDef> = {
  [K in UserPreferenceName]: (typeof userPreferenceDefinitions)[K] extends TDef ? K : never
}[UserPreferenceName]

type UserPreferenceBooleanDefinition<TName extends UserPreferenceBooleanName> =
  (typeof userPreferenceDefinitions)[TName]
type AnyUserPreferenceBooleanDefinition = UserPreferenceBooleanDefinition<UserPreferenceBooleanName>

type LegalEntityPreferenceDefinition<TName extends LegalEntityPreferenceName> =
  (typeof legalEntityPreferenceDefinitions)[TName]
type AnyLegalEntityPreferenceDefinition = LegalEntityPreferenceDefinition<LegalEntityPreferenceName>

type LegalEntityPreferenceNameFromDefinition<TDef> = {
  [K in LegalEntityPreferenceName]: (typeof legalEntityPreferenceDefinitions)[K] extends TDef
    ? K
    : never
}[LegalEntityPreferenceName]

const resolveUserPreference = (nameOrDef: UserPreferenceName | AnyUserPreferenceDefinition) =>
  typeof nameOrDef === "string" ? userPreferenceDefinitions[nameOrDef] : nameOrDef

const resolveBooleanPreference = (
  nameOrDef: UserPreferenceBooleanName | AnyUserPreferenceBooleanDefinition
) => (typeof nameOrDef === "string" ? userPreferenceDefinitions[nameOrDef] : nameOrDef)

const resolveLegalEntityPreference = (
  nameOrDef: LegalEntityPreferenceName | AnyLegalEntityPreferenceDefinition
) => (typeof nameOrDef === "string" ? legalEntityPreferenceDefinitions[nameOrDef] : nameOrDef)

export type PreferenceStoreState = {
  user: UserPreferenceValueMap
  legalEntity: LegalEntityPreferenceValueMap
}

export const { Context: PreferenceStoreContext, useSelector } =
  createScopedStore<PreferenceStoreState>()

// ─────────────────────────────────────────────────────────────────────────────
// JSON preference helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Safely parse a JSON string, returning undefined on failure */
export const parseJsonPreference = <T>(raw: string | undefined): T | undefined => {
  if (!raw) return undefined
  try {
    return JSON.parse(raw) as T
  } catch {
    return undefined
  }
}

/** Stringify a value for JSON storage */
export const stringifyJsonPreference = <T>(value: T): string => JSON.stringify(value)

// ─────────────────────────────────────────────────────────────────────────────
// Preference readers
// ─────────────────────────────────────────────────────────────────────────────

export const readUserPreferences = (session: FrontendSession): UserPreferenceValueMap => {
  const definitionKeys = keysOf(userPreferenceDefinitions)
  const entries = definitionKeys.map((name) => {
    const definition = userPreferenceDefinitions[name]
    const { key } = definition
    let value: unknown
    if (definition.kind === "boolean") {
      value = session.getBooleanUserPreference(key, definition.defaultValue ?? false)
    } else if (definition.kind === "json") {
      value = parseJsonPreference(session.getUserPreference<string>(key))
    } else {
      value = session.getUserPreference(key)
    }
    return [key, value] as const
  })

  return Object.fromEntries(entries) as UserPreferenceValueMap
}

export const readLegalEntityPreferences = (
  session: FrontendSession
): LegalEntityPreferenceValueMap => {
  const definitionKeys = keysOf(legalEntityPreferenceDefinitions)
  const entries = definitionKeys.map((name) => {
    const definition = legalEntityPreferenceDefinitions[name]
    const { key } = definition
    let value: unknown
    if (definition.kind === "json") {
      value = parseJsonPreference(session.getLegalEntityPreference<string>(key))
    } else {
      value = session.getLegalEntityPreference(key)
    }
    return [key, value] as const
  })

  return Object.fromEntries(entries) as LegalEntityPreferenceValueMap
}

// Overload: pass definition object (for those who prefer explicit references over string literals)
export function useUserPreferenceValue<
  TDef extends AnyUserPreferenceDefinition,
  TName extends UserPreferenceName = UserPreferenceNameFromDefinition<TDef>,
>(
  definition: TDef,
  defaultValue?: UserPreferenceValueByName<TName>
): PreferenceHookReturn<UserPreferenceValueByName<TName>>

// Overload: pass string name directly (cleaner call sites)
export function useUserPreferenceValue<TName extends UserPreferenceName>(
  name: TName,
  defaultValue?: UserPreferenceValueByName<TName>
): PreferenceHookReturn<UserPreferenceValueByName<TName>>

// Implementation
export function useUserPreferenceValue<TName extends UserPreferenceName>(
  nameOrDefinition: TName | AnyUserPreferenceDefinition,
  defaultValue?: UserPreferenceValueByName<TName>
): PreferenceHookReturn<UserPreferenceValueByName<TName>> {
  type TValue = UserPreferenceValueByName<TName>
  const session = useSession<FrontendSession>()
  const definition = resolveUserPreference(nameOrDefinition)
  const { key, kind } = definition
  const value = useSelector((state) => state.user[key] as TValue | undefined)
  const resolvedValue = value === undefined ? (defaultValue as TValue) : value

  // Helper to read current value with proper JSON parsing
  const getCurrentValue = React.useCallback(
    (): TValue | undefined =>
      kind === "json"
        ? parseJsonPreference<TValue>(session.getUserPreference<string>(key))
        : session.getUserPreference<TValue>(key),
    [key, kind, session]
  )

  // Supports functional updater to avoid stale closure issues when called multiple times
  // before React re-renders. The function receives the current value read directly from
  // session (always fresh) rather than captured React state (potentially stale).
  // Pass undefined to delete the preference.
  const setPreference = React.useCallback(
    (nextValueOrFn: TValue | undefined | ((prev: TValue | undefined) => TValue | undefined)) => {
      const nextValue =
        typeof nextValueOrFn === "function"
          ? (nextValueOrFn as (prev: TValue | undefined) => TValue | undefined)(getCurrentValue())
          : nextValueOrFn

      if (nextValue === undefined) {
        session.deleteUserPreference(key)
      } else if (kind === "json") {
        session.setUserPreference(key, stringifyJsonPreference(nextValue))
      } else {
        session.setUserPreference(key, nextValue)
      }
    },
    [key, kind, session, getCurrentValue]
  )

  return [resolvedValue, setPreference]
}

// Overload: pass definition object
export function useUserPreferenceBoolean<TDef extends AnyUserPreferenceBooleanDefinition>(
  definition: TDef,
  defaultValue?: boolean
): BooleanPreferenceHookReturn

// Overload: pass string name
export function useUserPreferenceBoolean(
  name: UserPreferenceBooleanName,
  defaultValue?: boolean
): BooleanPreferenceHookReturn

// Implementation
export function useUserPreferenceBoolean(
  nameOrDefinition: UserPreferenceBooleanName | AnyUserPreferenceBooleanDefinition,
  defaultValue: boolean = false
): BooleanPreferenceHookReturn {
  const session = useSession<FrontendSession>()
  const { key } = resolveBooleanPreference(nameOrDefinition)
  const value = useSelector((state) => state.user[key] as boolean | undefined)
  const resolvedValue = value ?? defaultValue

  // Pass undefined to delete the preference
  const setPreference = React.useCallback(
    (nextValueOrFn: boolean | undefined | ((prev: boolean | undefined) => boolean | undefined)) => {
      const nextValue =
        typeof nextValueOrFn === "function"
          ? nextValueOrFn(session.getUserPreference<boolean>(key))
          : nextValueOrFn

      if (nextValue === undefined) {
        session.deleteUserPreference(key)
      } else {
        session.setUserPreference(key, nextValue)
      }
    },
    [key, session]
  )

  return [resolvedValue, setPreference]
}

export const useEditLedgerPreference = () => {
  const { viewType } = useViewVersion()
  const [editLedger, setEditLedger] = useUserPreferenceBoolean("editLedger")
  const { hasAccessToAspect } = useSession<FrontendSession>()
  const hasInvoicing = hasAccessToAspect(AspectCode.Invoicing)
  const canEdit = viewType === ViewType.AIBookkeeper || hasInvoicing
  const location = useLocation()
  const isViewMode = new URLSearchParams(location.search).get("mode") === "view"
  return [!isViewMode && canEdit && editLedger, setEditLedger] as const
}

// Overload: pass definition object
export function useLegalEntityPreference<
  TDef extends AnyLegalEntityPreferenceDefinition,
  TName extends LegalEntityPreferenceName = LegalEntityPreferenceNameFromDefinition<TDef>,
>(
  definition: TDef,
  defaultValue?: LegalEntityPreferenceValueByName<TName>
): PreferenceHookReturn<LegalEntityPreferenceValueByName<TName>>

// Overload: pass string name
export function useLegalEntityPreference<TName extends LegalEntityPreferenceName>(
  name: TName,
  defaultValue?: LegalEntityPreferenceValueByName<TName>
): PreferenceHookReturn<LegalEntityPreferenceValueByName<TName>>

// Implementation
export function useLegalEntityPreference<TName extends LegalEntityPreferenceName>(
  nameOrDefinition: TName | AnyLegalEntityPreferenceDefinition,
  defaultValue?: LegalEntityPreferenceValueByName<TName>
): PreferenceHookReturn<LegalEntityPreferenceValueByName<TName>> {
  type TValue = LegalEntityPreferenceValueByName<TName>
  const session = useSession<FrontendSession>()
  const { key, kind } = resolveLegalEntityPreference(nameOrDefinition)
  const value = useSelector((state) => state.legalEntity[key] as TValue | undefined)
  const resolvedValue = value === undefined ? (defaultValue as TValue) : value

  // Helper to read current value with proper JSON parsing
  const getCurrentValue = React.useCallback(
    (): TValue | undefined =>
      kind === "json"
        ? parseJsonPreference<TValue>(session.getLegalEntityPreference<string>(key))
        : session.getLegalEntityPreference<TValue>(key),
    [key, kind, session]
  )

  // Supports functional updater to avoid stale closure issues when called multiple times
  // before React re-renders. The function receives the current value read directly from
  // session (always fresh) rather than captured React state (potentially stale).
  // Pass undefined to delete the preference.
  const setPreference = React.useCallback(
    (nextValueOrFn: TValue | undefined | ((prev: TValue | undefined) => TValue | undefined)) => {
      const nextValue =
        typeof nextValueOrFn === "function"
          ? (nextValueOrFn as (prev: TValue | undefined) => TValue | undefined)(getCurrentValue())
          : nextValueOrFn

      if (nextValue === undefined) {
        session.deleteLegalEntityPreference(key)
      } else if (kind === "json") {
        session.setLegalEntityPreference(key, stringifyJsonPreference(nextValue))
      } else {
        session.setLegalEntityPreference(key, nextValue)
      }
    },
    [key, kind, session, getCurrentValue]
  )

  return [resolvedValue, setPreference]
}
